/**
 * AlbertaSmallLandlords — dedicated SEO landing page for the tightest
 * wedge we can own: **small landlords (1–5 units) in Alberta**.
 *
 * Why this wedge:
 *   - Buildium / AppFolio price out small landlords ($70+ USD floor).
 *   - Alberta has no rent-control cap → higher revenue per unit than QC/ON.
 *   - RTDRS (Alberta's tenancy dispute resolution) is 2–4 weeks vs. LTB 6+ months.
 *   - No competitor targets this exact niche in Google search today.
 *
 * Route: /alberta-small-landlord-software
 */
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, MapPin, Check, ChevronRight } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";

const CITIES = ["Calgary", "Edmonton", "Red Deer", "Lethbridge", "St. Albert", "Sherwood Park", "Fort McMurray", "Medicine Hat"];

const PAINS = [
  { pain: "Chasing rent by e-transfer",     fix: "Automatic CAD rent collection with receipts and late-fee handling." },
  { pain: "Making sense of RTDRS forms",    fix: "Alberta-specific notice templates ready to serve or file." },
  { pain: "Digging through emails for T776",fix: "One-click T776 export mapped to the 12 CRA categories." },
  { pain: "Vetting a stranger on your own", fix: "Certn-powered credit + background check in 90 seconds." },
  { pain: "Handling maintenance calls",     fix: "Tenants send photos, you dispatch a vendor in one tap." },
];

const FAQ = [
  {
    q: "Is Central Rentals really built for a 2-unit Alberta landlord?",
    a: "Yes. Our Starter plan is $35/month whether you own 1 unit or 10. No per-door pricing, no setup fees.",
  },
  {
    q: "Do you support the RTDRS process?",
    a: "Yes. We generate Alberta-compliant notices (14-day for non-payment, 24-hour for substantial breach) and store the audit trail RTDRS asks for.",
  },
  {
    q: "How does rent collection work in Alberta?",
    a: "Tenants pay in CAD via Interac, card, or PAD. You get an instant deposit receipt and the ledger reconciles automatically. Stripe handles the transfer — PCI-DSS compliant.",
  },
  {
    q: "Can my accountant see the books?",
    a: "Yes. Invite your accountant with a view-only role from Settings → Team. They can export T776 or NR4 whenever they need.",
  },
];

export default function AlbertaSmallLandlords() {
  usePageTitle(
    "Small landlord software · Alberta (1–5 units) · Central Rentals",
    "The simplest Canadian property management software for Alberta landlords with 1–5 units. Rent collection in CAD, RTDRS-ready notices, T776 exports, and Certn tenant screening — from $35/month."
  );

  return (
    <div className="bg-white text-slate-900" data-testid="ab-small-landlords-page">
      {/* Local Business + FAQPage schema for Alberta */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SoftwareApplication",
                name: "Central Rentals — Alberta small landlord software",
                applicationCategory: "BusinessApplication",
                operatingSystem: "Web",
                offers: { "@type": "Offer", price: "60", priceCurrency: "CAD" },
                areaServed: { "@type": "AdministrativeArea", name: "Alberta, Canada" },
              },
              {
                "@type": "FAQPage",
                mainEntity: FAQ.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <PublicHeader light />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-16">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-semibold tracking-wide text-emerald-800">
          <MapPin className="w-4 h-4"/> For Alberta landlords · 1–5 units
        </div>
        <h1 className="font-display mt-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tighter text-slate-900">
          Small landlord software,{" "}
          <span className="bg-gradient-to-r from-emerald-600 via-teal-500 to-sky-500 bg-clip-text text-transparent">built for Alberta.</span>
        </h1>
        <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
          One flat $35/month covers 1 to 10 units. RTDRS-ready notices, T776 tax exports, and Certn tenant screening — no per-door fees, no US pricing math.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/signup?role=landlord"
            data-testid="ab-signup-cta"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base shadow-lg shadow-emerald-600/30"
          >
            Start free — 60 seconds <ArrowRight className="w-5 h-5"/>
          </Link>
          <Link
            to="/pricing"
            data-testid="ab-pricing-cta"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border-2 border-slate-200 hover:border-slate-400 text-slate-900 font-bold text-base"
          >
            See pricing
          </Link>
        </div>
      </section>

      {/* Pains */}
      <section className="bg-slate-50 border-y border-slate-200 py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tighter text-slate-900">
            The 5 things that eat your weekend — automated.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {PAINS.map((p, i) => (
              <div key={i} data-testid={`ab-pain-${i}`} className="p-6 rounded-2xl bg-white border border-slate-200">
                <div className="text-sm uppercase tracking-widest font-bold text-rose-600">Pain</div>
                <div className="text-lg font-bold text-slate-900 mt-1">{p.pain}</div>
                <div className="mt-4 text-sm uppercase tracking-widest font-bold text-emerald-600">Fixed by Central Rentals</div>
                <div className="text-base text-slate-700 mt-1">{p.fix}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cities served */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tighter">Serving landlords across Alberta.</h2>
        <div className="mt-6 flex flex-wrap gap-2">
          {CITIES.map((c) => (
            <span key={c} data-testid={`ab-city-${c.toLowerCase().replace(/\s|\./g, "")}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-sm font-semibold">
              <Check className="w-4 h-4"/> {c}
            </span>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-y border-slate-200 py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <h2 className="font-display text-2xl md:text-4xl font-bold text-slate-900 tracking-tighter">Common questions</h2>
          <div className="mt-8 space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} data-testid={`ab-faq-${i}`} className="rounded-xl bg-white border border-slate-200 p-5">
                <summary className="cursor-pointer text-base font-bold text-slate-900 flex items-center justify-between">
                  {f.q}
                  <ChevronRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform"/>
                </summary>
                <p className="mt-3 text-base text-slate-700 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-bold uppercase tracking-widest text-emerald-800">
          <ShieldCheck className="w-4 h-4"/> Canadian-owned · PIPEDA-compliant
        </div>
        <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tighter">
          Ready in 60 seconds.
        </h2>
        <p className="mt-4 text-base md:text-lg text-slate-600">Start free. Cancel any time. Cancel any time.</p>
        <Link
          to="/signup?role=landlord"
          data-testid="ab-bottom-cta"
          className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base shadow-lg shadow-emerald-600/30"
        >
          Create my Alberta landlord account <ArrowRight className="w-5 h-5"/>
        </Link>
      </section>
    </div>
  );
}
